import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import Settings from '@mui/icons-material/Settings';
import BugReport from '@mui/icons-material/BugReport';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import AccountTree from '@mui/icons-material/AccountTree';
import Lightbulb from '@mui/icons-material/Lightbulb';
import CreditCard from '@mui/icons-material/CreditCard';
import useLocales from 'hooks/useLocales';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// material
import { alpha } from '@mui/material/styles';
import { Button, Box, Divider, MenuItem, Typography } from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useIsMountedRef from '../../hooks/useIsMountedRef';
// components
import { MIconButton } from '../../components/@material-extend';
import MyAvatar from '../../components/MyAvatar';
import MenuPopover from '../../components/MenuPopover';
// utils
import { isAdminRole } from '../../common/adminRoles';

export default function AccountPopover() {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const { user, logout } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout?.();
      if (isMountedRef.current) {
        navigate('/');
        handleClose();
      }
    } catch {
      enqueueSnackbar('Unable to logout', { variant: 'error', autoHideDuration: 6000 });
    }
  };

  const isAdmin = isAdminRole(user?.registeredRole);

  const MAIN_OPTIONS = [
    {
      label: translate('menu.settings'),
      icon: Settings,
      linkTo: PATH_DASHBOARD.user.account
    },
    {
      label: translate('menu.report_problem'),
      icon: BugReport,
      linkTo: PATH_DASHBOARD.user.report
    }
  ];

  const ADMIN_OPTIONS = [
    {
      label: translate('menu.admin'),
      icon: AdminPanelSettings,
      linkTo: PATH_DASHBOARD.admin.root
    },
    {
      label: translate('menu.learning_cycles'),
      icon: AutoStoriesOutlinedIcon,
      linkTo: PATH_DASHBOARD.admin.learningCycles
    }
  ];

  const EXPERIMENTAL_OPTIONS = [
    {
      label: translate('menu.graph_view'),
      icon: AccountTree,
      linkTo: PATH_DASHBOARD.graph.root
    },
    {
      label: translate('menu.suggestions'),
      icon: Lightbulb,
      linkTo: PATH_DASHBOARD.explore.suggestions
    },
    {
      label: translate('menu.external_platforms'),
      icon: CreditCard,
      linkTo: PATH_DASHBOARD.externalPlatforms.root
    }
  ];

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 40,
          height: 40,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        <MyAvatar sx={{ width: 32, height: 32 }} />
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        {isAdmin && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography
              variant="caption"
              sx={{
                px: 2.5,
                color: 'text.disabled',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em'
              }}
            >
              {translate('menu.administration')}
            </Typography>
            {ADMIN_OPTIONS.map((option) => (
              <MenuItem
                key={option.label}
                to={option.linkTo}
                component={RouterLink}
                onClick={handleClose}
                sx={{ typography: 'body2', py: 1, px: 2.5 }}
              >
                <Box component={option.icon} sx={{ mr: 2, fontSize: 24 }} />
                {option.label}
              </MenuItem>
            ))}
          </>
        )}

        <Divider sx={{ my: 1 }} />

        {MAIN_OPTIONS.map((option) => (
          <MenuItem
            key={option.label}
            to={option.linkTo}
            component={RouterLink}
            onClick={handleClose}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Box component={option.icon} sx={{ mr: 2, fontSize: 24 }} />
            {option.label}
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />

        <Typography
          variant="caption"
          sx={{
            px: 2.5,
            color: 'text.disabled',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em'
          }}
        >
          {translate('menu.experimental')}
        </Typography>

        {EXPERIMENTAL_OPTIONS.map((option) => (
          <MenuItem
            key={option.label}
            to={option.linkTo}
            component={RouterLink}
            onClick={handleClose}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Box component={option.icon} sx={{ mr: 2, fontSize: 24 }} />
            {option.label}
          </MenuItem>
        ))}

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button fullWidth size="small" color="inherit" variant="outlined" onClick={handleLogout}>
            {translate('components.account_popover.logout')}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
